/* eslint-disable no-console */

// Captura central de erros do totem: Firestore (coleção `erros`, lida pelo
// suporte) e PostHog (`$exception`, agrupado por totem e por compra).
//
// Regra de ouro: reportar um erro NUNCA pode atrapalhar o cliente nem travar o
// totem. Por isso este módulo:
//   - nunca lança: tudo roda dentro de try/catch e os envios são promessas
//     soltas com .catch próprio;
//   - não bloqueia: quem chama recebe o controle de volta na hora; o envio sai
//     num setTimeout(0), fora do fluxo da tela (inclusive fora de um render
//     que acabou de quebrar);
//   - não faz enxurrada: o mesmo erro só sai uma vez por janela (as repetições
//     vão contadas no envio seguinte) e há um teto de envios por minuto — um
//     erro em laço de render não vira milhares de gravações no 4G;
//   - não acumula memória: offline, o Firestore guarda as gravações na fila
//     dele até a rede voltar; acima de um teto de gravações não confirmadas,
//     novas vão só para o PostHog (que tem fila e retry próprios);
//   - não importa store, router nem firebase: o contexto (totem, compra, rota)
//     e o destino do Firestore são injetados pelo main.js. Assim o módulo pode
//     ser carregado antes de tudo e capturar erro do boot, que fica guardado
//     num buffer pequeno até os destinos estarem prontos.

import posthog, { posthogAtivo } from "@/posthog";

const LIMITE_MENSAGEM = 1000;
const LIMITE_STACK = 4000;
const LIMITE_INFO = 500;
const LIMITE_EXTRA = 2000;

const JANELA_DEDUP_MS = 60 * 1000;
const JANELA_DEDUP_CONSOLE_MS = 10 * 60 * 1000;
const MAX_ENVIOS_POR_MINUTO = 20;
const MAX_CONSOLE_POR_MINUTO = 10;
const MAX_FIRESTORE_PENDENTES = 30;
const MAX_BUFFER_BOOT = 20;
const MAX_FINGERPRINTS = 200;

// Ruído conhecido: não é bug do app e não ajuda o suporte.
const IGNORAR = [
  /ResizeObserver loop/i,
  // Navegação recusada/redirecionada pelo vue-router (guardas, mesmo destino).
  /Avoided redundant navigation|Redirected when going from|Navigation (cancelled|aborted)|NavigationDuplicated/i,
  // Erro de script de outra origem: o navegador esconde tudo, não há o que ler.
  /^Script error\.?$/i,
];

// Logs internos dos SDKs (PostHog, Firebase offline etc.) que passam pelo
// console.error. Encaminhar isso seria eco, e o do PostHog seria laço.
const IGNORAR_CONSOLE = [
  /^\[PostHog/i,
  /@firebase\//i,
  /^\[monitoramentoErros\]/,
];

const consoleOriginal = {
  error: console.error.bind(console),
  warn: console.warn.bind(console),
};

let destinoFirestore = null;
let obterContexto = () => ({});
let habilitado = true;
let pronto = false;

const bufferBoot = [];
const ultimoEnvio = new Map(); // fingerprint → { em, suprimidos }
const enviosRecentes = { erro: [], console: [] };
let firestorePendentes = 0;
let reportando = false;
let reportadosDireto = new WeakSet();

function ehObjeto(valor) {
  return typeof valor === "object" && valor !== null;
}

function truncar(texto, limite) {
  if (texto === null || texto === undefined) return null;
  const s = String(texto);
  return s.length > limite
    ? `${s.slice(0, limite)}… [+${s.length - limite}]`
    : s;
}

function serializarSeguro(valor, limite) {
  try {
    const vistos = new WeakSet();
    const json = JSON.stringify(valor, (_, v) => {
      if (typeof v === "object" && v !== null) {
        if (vistos.has(v)) return "[circular]";
        vistos.add(v);
      }
      if (typeof v === "function") return undefined;
      return v;
    });
    return truncar(json, limite);
  } catch (_) {
    try {
      return truncar(String(valor), limite);
    } catch (__) {
      return "[não serializável]";
    }
  }
}

// Aceita qualquer coisa que chegue num catch: Error, string, resposta do axios
// (o interceptor do main.js rejeita com `error.response`), undefined, objeto.
export function normalizarErro(erro) {
  if (erro instanceof Error || (erro && typeof erro.message === "string")) {
    return {
      nome: truncar(erro.name || "Error", 100),
      mensagem: truncar(erro.message || String(erro), LIMITE_MENSAGEM),
      stack: truncar(erro.stack, LIMITE_STACK),
      codigo: erro.code !== undefined ? truncar(erro.code, 100) : null,
      extra: null,
    };
  }
  if (typeof erro === "string") {
    return {
      nome: "Mensagem",
      mensagem: truncar(erro, LIMITE_MENSAGEM),
      stack: null,
      codigo: null,
      extra: null,
    };
  }
  if (erro === undefined || erro === null) {
    return {
      nome: "SemMotivo",
      mensagem:
        "Erro sem detalhes (undefined/null) — provável falha de rede sem resposta",
      stack: null,
      codigo: null,
      extra: null,
    };
  }
  // Resposta HTTP do axios: status e URL bastam; o corpo vai curto.
  if (
    typeof erro === "object" &&
    "status" in erro &&
    ("config" in erro || "data" in erro)
  ) {
    const url = erro.config && erro.config.url;
    return {
      nome: "HttpError",
      mensagem: truncar(
        `HTTP ${erro.status} ${erro.config ? erro.config.method || "" : ""} ${
          url || ""
        }`.trim(),
        LIMITE_MENSAGEM
      ),
      stack: null,
      codigo: truncar(erro.status, 20),
      extra: serializarSeguro(erro.data, LIMITE_EXTRA),
    };
  }
  return {
    nome: "Objeto",
    mensagem: serializarSeguro(erro, LIMITE_MENSAGEM),
    stack: null,
    codigo: null,
    extra: null,
  };
}

function deveIgnorar(normalizado, origem) {
  const texto = `${normalizado.nome}: ${normalizado.mensagem}`;
  if (
    IGNORAR.some((re) => re.test(normalizado.mensagem || "") || re.test(texto))
  )
    return true;
  if (
    origem === "console" &&
    IGNORAR_CONSOLE.some((re) => re.test(normalizado.mensagem || ""))
  )
    return true;
  return false;
}

function dentroDoLimite(tipo, agora) {
  const lista = enviosRecentes[tipo];
  while (lista.length && agora - lista[0] > 60 * 1000) lista.shift();
  const max =
    tipo === "console" ? MAX_CONSOLE_POR_MINUTO : MAX_ENVIOS_POR_MINUTO;
  if (lista.length >= max) return false;
  lista.push(agora);
  return true;
}

// Retorna quantas repetições foram suprimidas desde o último envio, ou -1 se
// este ainda está dentro da janela (e deve ser só contado).
function controlarRepeticao(fingerprint, agora, janela) {
  const anterior = ultimoEnvio.get(fingerprint);
  if (anterior && agora - anterior.em < janela) {
    anterior.suprimidos += 1;
    return -1;
  }
  const suprimidos = anterior ? anterior.suprimidos : 0;
  ultimoEnvio.delete(fingerprint);
  ultimoEnvio.set(fingerprint, { em: agora, suprimidos: 0 });
  if (ultimoEnvio.size > MAX_FINGERPRINTS) {
    ultimoEnvio.delete(ultimoEnvio.keys().next().value);
  }
  return suprimidos;
}

function contextoSeguro() {
  let ctx = {};
  try {
    ctx = obterContexto() || {};
  } catch (_) {
    ctx = {};
  }
  let totemLembrado = null;
  try {
    totemLembrado = localStorage.getItem("totemId");
  } catch (_) {
    totemLembrado = null;
  }
  let rota = ctx.rota || null;
  if (!rota) {
    try {
      rota = window.location.pathname;
    } catch (_) {
      rota = null;
    }
  }
  let online = null;
  try {
    online = typeof navigator !== "undefined" ? navigator.onLine : null;
  } catch (_) {
    online = null;
  }
  return {
    totem_id: ctx.totemId || totemLembrado || "Sem definição",
    compra_id: ctx.compraId || null,
    rota: truncar(rota, 300),
    online,
  };
}

function enviarFirestore(payload) {
  if (!destinoFirestore) return;
  if (firestorePendentes >= MAX_FIRESTORE_PENDENTES) return;
  firestorePendentes += 1;
  // Promise.resolve().then: erro síncrono do SDK vira rejeição tratada aqui.
  Promise.resolve()
    .then(() => destinoFirestore(payload))
    .catch((falha) => {
      consoleOriginal.warn(
        "[monitoramentoErros] falha ao gravar no Firestore:",
        falha && falha.message
      );
    })
    .finally(() => {
      firestorePendentes -= 1;
    });
}

function enviarPostHog(erroOriginal, normalizado, propriedades) {
  if (!posthogAtivo) return;
  try {
    const erro =
      erroOriginal instanceof Error
        ? erroOriginal
        : Object.assign(new Error(normalizado.mensagem || "Erro"), {
            name: normalizado.nome || "Error",
          });
    posthog.captureException(erro, propriedades);
  } catch (falha) {
    consoleOriginal.warn(
      "[monitoramentoErros] falha ao enviar ao PostHog:",
      falha && falha.message
    );
  }
}

function processar(item) {
  const { erro, opcoes, em } = item;
  const origem = opcoes.origem || "manual";
  const nivel = opcoes.nivel || "erro";
  // Padrão comum nas telas: `console.error(err)` seguido de `sendOneError(err)`.
  // O reporte direto é o que vale; o eco do console é descartado.
  if (origem === "console" && ehObjeto(erro) && reportadosDireto.has(erro))
    return;
  const normalizado = normalizarErro(erro);
  if (deveIgnorar(normalizado, origem)) return;

  const info = truncar(opcoes.info, LIMITE_INFO);
  const fingerprint = `${origem}|${normalizado.nome}|${(
    normalizado.mensagem || ""
  ).slice(0, 200)}|${info || ""}`;
  const agora = Date.now();
  const janela =
    origem === "console" ? JANELA_DEDUP_CONSOLE_MS : JANELA_DEDUP_MS;
  const suprimidos = controlarRepeticao(fingerprint, agora, janela);
  if (suprimidos < 0) return;
  if (!dentroDoLimite(origem === "console" ? "console" : "erro", agora)) return;

  const ctx = contextoSeguro();
  const versao = process.env.VUE_APP_VERSION || null;

  // Erros de console são falhas já tratadas pelas telas: vão só ao PostHog,
  // para a coleção `erros` continuar sendo sinal, não ruído.
  if (origem !== "console") {
    enviarFirestore({
      // Campos legados lidos pelo suporte.
      totem_id: ctx.totem_id,
      time: new Date(em),
      error: normalizado.mensagem || "undefined",
      info: info || "undefined",
      route: ctx.rota || "undefined",
      // Campos novos.
      nome: normalizado.nome,
      stack: normalizado.stack,
      codigo: normalizado.codigo,
      extra: normalizado.extra,
      origem,
      nivel,
      compra_id: ctx.compra_id,
      versao_app: versao,
      online: ctx.online,
      repeticoes_suprimidas: suprimidos,
    });
  }

  enviarPostHog(erro, normalizado, {
    origem,
    nivel,
    info,
    rota: ctx.rota,
    online: ctx.online,
    versao_app: versao,
    codigo: normalizado.codigo,
    repeticoes_suprimidas: suprimidos,
  });
}

function agendar(item) {
  try {
    setTimeout(() => {
      reportando = true;
      try {
        processar(item);
      } catch (falha) {
        consoleOriginal.warn(
          "[monitoramentoErros] falha interna ao reportar:",
          falha && falha.message
        );
      } finally {
        reportando = false;
      }
    }, 0);
  } catch (_) {
    // Sem setTimeout não há o que fazer; o erro já foi para o console.
  }
}

/**
 * Reporta um erro sem bloquear e sem lançar. Pode ser chamado de qualquer
 * lugar, a qualquer momento (até antes do app montar).
 *
 * @param {*} erro Error, string, resposta do axios, qualquer coisa.
 * @param {{ origem?: string, info?: string, nivel?: "erro"|"aviso" }} opcoes
 */
export function reportarErro(erro, opcoes = {}) {
  try {
    if (!habilitado) return;
    const item = { erro, opcoes: opcoes || {}, em: Date.now() };
    if (item.opcoes.origem !== "console" && ehObjeto(erro))
      reportadosDireto.add(erro);
    if (!pronto) {
      if (bufferBoot.length < MAX_BUFFER_BOOT) bufferBoot.push(item);
      return;
    }
    agendar(item);
  } catch (_) {
    // Nunca propaga.
  }
}

function aoErroGlobal(evento) {
  try {
    // Erro de carregamento de recurso (<img>, <script>) não tem `error`.
    const erro = evento && (evento.error || evento.message);
    if (!erro) return;
    reportarErro(erro, {
      origem: "window.onerror",
      info: evento.filename
        ? `${evento.filename}:${evento.lineno}:${evento.colno}`
        : null,
    });
  } catch (_) {
    // Nunca propaga.
  }
}

function aoRejeicaoSolta(evento) {
  try {
    reportarErro(evento ? evento.reason : undefined, {
      origem: "unhandledrejection",
    });
  } catch (_) {
    // Nunca propaga.
  }
}

let consoleInterceptado = false;
function interceptarConsoleError() {
  if (consoleInterceptado) return;
  consoleInterceptado = true;
  console.error = function (...args) {
    consoleOriginal.error(...args);
    try {
      if (reportando || !args.length) return;
      const erro = args.find((a) => a instanceof Error);
      const texto = args
        .filter((a) => !(a instanceof Error))
        .map((a) => (typeof a === "string" ? a : serializarSeguro(a, 300)))
        .join(" ");
      if (erro) {
        reportarErro(erro, {
          origem: "console",
          info: texto || null,
          nivel: "aviso",
        });
      } else {
        reportarErro(texto, { origem: "console", nivel: "aviso" });
      }
    } catch (_) {
      // Nunca propaga.
    }
  };
}

let listenersRegistrados = false;

/**
 * Liga a captura global (window.onerror e promessas rejeitadas sem catch).
 * Chamar o quanto antes no boot; erros ficam no buffer até `configurar`.
 */
export function iniciarCapturaGlobal({ interceptarConsole = true } = {}) {
  if (listenersRegistrados || typeof window === "undefined") return;
  listenersRegistrados = true;
  try {
    window.addEventListener("error", aoErroGlobal);
    window.addEventListener("unhandledrejection", aoRejeicaoSolta);
    if (interceptarConsole) interceptarConsoleError();
  } catch (_) {
    // Nunca propaga.
  }
}

/**
 * Injeta os destinos e o contexto e despeja o buffer do boot.
 *
 * @param {{
 *   gravarFirestore?: (payload: object) => Promise<unknown>,
 *   contexto?: () => { totemId?: string, compraId?: string, rota?: string },
 *   ativo?: boolean,
 * }} config
 */
export function configurarMonitoramento(config = {}) {
  try {
    if (config.gravarFirestore) destinoFirestore = config.gravarFirestore;
    if (config.contexto) obterContexto = config.contexto;
    if (config.ativo === false) {
      habilitado = false;
      bufferBoot.length = 0;
      return;
    }
    pronto = true;
    bufferBoot.splice(0).forEach(agendar);
  } catch (_) {
    // Nunca propaga.
  }
}

// Só para testes.
export function __resetarMonitoramento() {
  destinoFirestore = null;
  obterContexto = () => ({});
  habilitado = true;
  pronto = false;
  bufferBoot.length = 0;
  ultimoEnvio.clear();
  enviosRecentes.erro.length = 0;
  enviosRecentes.console.length = 0;
  firestorePendentes = 0;
  reportando = false;
  reportadosDireto = new WeakSet();
}
